export class HTLCPublicClient {
    constructor() {
        this.consensusOptions = { minQuorum: 2, batchSize: 3 };
    }
    async getSolverLockDetailsWithConsensus(params, nodeUrls, options) {
        const minQuorum = options?.minQuorum ?? this.consensusOptions.minQuorum;
        const batchSize = options?.batchSize ?? this.consensusOptions.batchSize;
        const prefetchedResult = options?.prefetchedResult;
        if (!nodeUrls.length && !prefetchedResult)
            return null;
        const effectiveQuorum = Math.min(minQuorum, nodeUrls.length);
        // Skip nodeUrls[0] when prefetched — it already represents that node's result
        const urlsToQuery = prefetchedResult ? nodeUrls.slice(1) : nodeUrls;
        // Partition urlsToQuery into batches
        const batches = [];
        for (let i = 0; i < urlsToQuery.length; i += batchSize) {
            batches.push(urlsToQuery.slice(i, i + batchSize));
        }
        const allValidResults = prefetchedResult ? [prefetchedResult] : [];
        let totalQueried = prefetchedResult ? 1 : 0;
        let lastError = null;
        // Prefetched alone satisfies quorum (e.g. Aztec minQuorum=1)
        if (allValidResults.length >= effectiveQuorum) {
            return { details: allValidResults[0], agreedCount: allValidResults.length };
        }
        for (const batch of batches) {
            const results = await Promise.allSettled(batch.map(url => this.getSolverLockDetails(params, url)));
            totalQueried += batch.length;
            const fulfilled = results.filter((r) => r.status === 'fulfilled');
            const validResults = fulfilled.map(r => r.value).filter((r) => r !== null);
            allValidResults.push(...validResults);
            const batchError = results.find((r) => r.status === 'rejected');
            if (batchError)
                lastError = batchError.reason;
            if (allValidResults.length >= effectiveQuorum) {
                const [first, ...rest] = allValidResults;
                if (rest.length > 0 && !rest.every(r => solverLockDetailsMatch(r, first))) {
                    throw new Error('Lock details do not match across the provided nodes');
                }
                return { details: first, agreedCount: allValidResults.length };
            }
        }
        // All batches exhausted
        if (allValidResults.length === 0) {
            if (lastError)
                throw lastError;
            return null;
        }
        throw new Error(`Insufficient node agreement: ${allValidResults.length} of ${totalQueried} nodes returned results, need at least ${effectiveQuorum}`);
    }
}
/**
 * Do two readings of the same solver lock agree on its *terms* — everything that
 * defines the deal the user is about to hand a secret for? Excludes `status` and
 * `secret`, which legitimately change over the lock's lifetime, so this is the
 * right predicate for comparing readings taken at different times (e.g. a
 * light-client verdict against a later RPC poll).
 */
export function solverLockTermsMatch(left, right) {
    return amountsMatch(left, right) &&
        left.hashlock.toLowerCase() === right.hashlock.toLowerCase() &&
        left.sender === right.sender &&
        left.recipient === right.recipient &&
        left.token === right.token &&
        left.refundTo === right.refundTo &&
        left.payoutCurve === right.payoutCurve &&
        left.payoutCurveData === right.payoutCurveData &&
        left.timelock === right.timelock;
}
/**
 * Full point-in-time equality: the lock's terms plus its current status. Used to
 * compare readings taken simultaneously across nodes, where status must agree too.
 */
export function solverLockDetailsMatch(left, right) {
    return solverLockTermsMatch(left, right) && left.status === right.status;
}
function amountsMatch(left, right) {
    if (left.amountInBaseUnits !== undefined || right.amountInBaseUnits !== undefined) {
        return left.amountInBaseUnits !== undefined &&
            right.amountInBaseUnits !== undefined &&
            left.amountInBaseUnits === right.amountInBaseUnits;
    }
    return String(left.amount) === String(right.amount);
}
